import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, FileText, ArrowCounterClockwise, Truck, Phone, CaretRight } from '@phosphor-icons/react';

// Styled inline link used throughout the legal copy
const L = ({ to, href, children, external = false }) => {
  const cls = "text-[#bc64aa] underline decoration-[#bc64aa]/30 underline-offset-2 hover:decoration-[#bc64aa] transition-colors";
  if (href) {
    return <a href={href} className={cls} {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}>{children}</a>;
  }
  return <Link to={to} className={cls}>{children}</Link>;
};

// All 5 legal policies as separate sub-pages under /legal/:slug
// Each has its own unique URL (e.g. /legal/privacy-policy) so Razorpay's
// verification crawler can find them independently in the footer.
const sections = [
  {
    slug: 'privacy-policy',
    label: 'Privacy Policy',
    icon: ShieldCheck,
    summary: 'What data we collect and how we use it.',
    updated: 'February 18, 2026',
    body: [
      {
        h: 'Introduction',
        p: 'Veveru ("we", "us", or "our") operates veveru.com and the mobile web experience (the "Service"). This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service.'
      },
      {
        h: 'Information We Collect',
        p: 'We collect the following personal information when you interact with our site:',
        list: [
          'Contact data — name, email address, phone number, shipping address',
          'Order data — products purchased, quantities, transaction amounts',
          'Payment data — handled and stored by our payment partner Razorpay; we do not store card numbers or CVV on our servers',
          <>Inquiry data — messages, design files, and requirements you submit via <L to="/custom-inquiry">Custom Order</L>, <L to="/bulk-inquiry">Bulk Inquiry</L>, or <L to="/partner">Partner With Us</L> forms</>,
          'Technical data — IP address, browser type, device information, and cookies used for session management'
        ]
      },
      {
        h: 'How We Use Your Information',
        p: 'Your information is used to fulfil orders, respond to inquiries, process payments, provide customer support, send order-status updates, and improve our products and services. We do not sell your personal information to third parties.'
      },
      {
        h: 'Data Sharing',
        p: 'We share limited data only with the following trusted partners, strictly for fulfilling your order:',
        list: [
          'Delhivery — for shipping and logistics',
          'Razorpay — for payment processing',
          <>Cloud storage providers — for hosting design files you upload with <L to="/custom-inquiry">Custom</L> or <L to="/bulk-inquiry">Bulk</L> inquiries</>
        ]
      },
      {
        h: 'Cookies',
        p: 'We use essential cookies to keep you logged in and remember your cart. We do not use third-party advertising cookies.'
      },
      {
        h: 'Data Security',
        p: 'We use industry-standard measures to protect your data, including HTTPS encryption in transit and access controls for our internal systems. No method of electronic storage is 100% secure, but we continuously review our practices.'
      },
      {
        h: 'Your Rights',
        p: <>You may request access to, correction of, or deletion of your personal data at any time by writing to <L href="mailto:purchase@modshell.live">purchase@modshell.live</L>. We will respond within 30 days.</>
      },
      {
        h: 'Children\'s Privacy',
        p: 'Our Service is not directed at anyone under the age of 18. We do not knowingly collect data from children.'
      },
      {
        h: 'Changes to this Policy',
        p: 'We may update this policy from time to time. The "Last updated" date at the top of this page will reflect any changes. Material changes will be communicated via email to registered customers.'
      },
      {
        h: 'Contact',
        p: <>Questions about this Privacy Policy? Email <L href="mailto:purchase@modshell.live">purchase@modshell.live</L> or call <L href="tel:+916374302212">+91 6374 302 212</L>.</>
      }
    ]
  },
  {
    slug: 'terms-and-conditions',
    label: 'Terms & Conditions',
    icon: FileText,
    summary: 'The rules and agreements for using our site.',
    updated: 'February 18, 2026',
    body: [
      {
        h: 'Acceptance of Terms',
        p: 'By accessing or using veveru.com, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the Service.'
      },
      {
        h: 'Products & Pricing',
        p: 'All products listed are FRP (Fiber Reinforced Polymer) planters manufactured by Veveru. Product images are for representational purposes; actual colour may vary slightly due to display variance and the hand-finished nature of each piece.',
        list: [
          'Prices on the website are exclusive of GST. 18% GST is added at checkout.',
          'Prices are subject to change without prior notice.',
          <>Bulk orders may qualify for discounts depending on quantity — please use the <L to="/bulk-inquiry">Bulk Inquiry</L> form.</>
        ]
      },
      {
        h: 'Orders',
        p: 'Placement of an order constitutes an offer to purchase. We reserve the right to accept or decline any order. Upon acceptance, you will receive an order confirmation via email. Production timelines are 10–14 working days for standard orders of 5–10 planters, and 14–18 working days for custom or bulk orders.'
      },
      {
        h: 'Warranty',
        p: 'We provide a 3-year warranty on the paint finish of our planters — the colour will not fade when used indoors or outdoors. The planter body itself does not carry a separate warranty, as FRP is inherently long-lasting and does not crack, warp, or degrade under normal use.'
      },
      {
        h: 'User Accounts',
        p: <>You are responsible for maintaining the confidentiality of your account password and for all activities under your account. Notify us immediately at <L href="mailto:purchase@modshell.live">purchase@modshell.live</L> if you suspect unauthorised access.</>
      },
      {
        h: 'Intellectual Property',
        p: 'All content on this site — including photography, design sketches, product names, and copy — is the property of Veveru and is protected by applicable intellectual property laws. You may not copy or reuse any content without written permission.'
      },
      {
        h: 'Prohibited Use',
        p: 'You agree not to use the Service to:',
        list: [
          'Violate any applicable law or regulation',
          'Upload malicious code or disrupt site functionality',
          'Impersonate another person or misrepresent your affiliation',
          'Scrape or extract data via automated means'
        ]
      },
      {
        h: 'Limitation of Liability',
        p: 'Veveru shall not be liable for indirect, incidental, or consequential damages arising out of or in connection with your use of the Service, to the maximum extent permitted by law.'
      },
      {
        h: 'Governing Law',
        p: 'These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the courts in Coimbatore, Tamil Nadu.'
      },
      {
        h: 'Contact',
        p: <>Questions about these Terms? Email <L href="mailto:purchase@modshell.live">purchase@modshell.live</L>.</>
      }
    ]
  },
  {
    slug: 'refund-policy',
    label: 'Cancellation & Refund Policy',
    icon: ArrowCounterClockwise,
    summary: 'Cancellations, damaged goods and refund process.',
    updated: 'February 18, 2026',
    body: [
      {
        h: 'Order Cancellation',
        p: 'Orders can be cancelled free of charge before they enter production. Because each planter is made to order, cancellations requested after production has begun may incur a partial charge:',
        list: [
          'Within 24 hours of placing the order — full refund, no questions asked',
          'After 24 hours but before dispatch — 80% refund; 20% covers material and labour already incurred',
          'After dispatch — cancellation not possible; please refer to the damaged-goods section below if applicable'
        ]
      },
      {
        h: 'Damaged or Defective Goods',
        p: 'We take great care in packaging your planters in multiple layers of bubble wrap and reinforced boxes. However, if you receive a product in a damaged condition:',
        list: [
          'Take clear photographs of the damaged area immediately upon opening the package',
          <>Email the photographs along with your Order ID to <L href="mailto:purchase@modshell.live">purchase@modshell.live</L> within 24 hours of delivery</>,
          'Our team will review the claim and arrange either a free replacement or a full refund, at your preference',
          'Claims raised after 24 hours of delivery may not be honoured, as damage cannot be verified post that window'
        ]
      },
      {
        h: 'Refund Process',
        p: 'Approved refunds are processed within 7–10 working days and credited back to the original payment method. UPI and net-banking refunds typically reach you in 3–5 working days; card refunds may take 7–10 working days depending on your bank.'
      },
      {
        h: 'Non-Refundable Items',
        p: 'Custom orders and bulk orders — where the planter has been produced to your specific colour, size, texture, or design specifications — are non-refundable unless received damaged. Please confirm all specifications carefully before placing a custom order.'
      },
      {
        h: 'Contact',
        p: <>Questions about a cancellation or refund? Email <L href="mailto:purchase@modshell.live">purchase@modshell.live</L> or call <L href="tel:+916374302212">+91 6374 302 212</L> between 10 AM and 6 PM, Monday to Saturday.</>
      }
    ]
  },
  {
    slug: 'shipping-policy',
    label: 'Shipping Policy',
    icon: Truck,
    summary: 'Delivery timelines, logistics partner and packaging.',
    updated: 'February 18, 2026',
    body: [
      {
        h: 'Logistics Partner',
        p: 'We have partnered with Delhivery, one of India\'s most reliable logistics providers, for door-to-door delivery of all orders.'
      },
      {
        h: 'Delivery Timelines',
        p: 'Because every Veveru planter is made to order, timelines depend on quantity and whether the order is standard or custom:',
        list: [
          'Standard orders (5–10 planters) — 10 to 14 working days from order confirmation',
          'Custom orders (colour, size, texture specification) — 14 to 18 working days',
          'Bulk orders — timeline depends on quantity; our team will confirm during inquiry',
          'Transit time after dispatch — typically 3–5 working days within India'
        ]
      },
      {
        h: 'Serviceable Areas',
        p: <>We ship pan-India. For international shipping or remote locations that Delhivery does not serve, please use the <L to="/bulk-inquiry">Bulk Inquiry</L> form so we can arrange logistics manually.</>
      },
      {
        h: 'Shipping Charges',
        p: 'Shipping charges depend on order weight, destination pincode, and order value. The exact amount is calculated and displayed during checkout before payment. Orders above ₹5,000 may qualify for free shipping — promotional offers will be visible at checkout.'
      },
      {
        h: 'Packaging',
        p: 'Each planter is carefully wrapped in multiple layers of protective bubble wrap and housed in durable, reinforced boxes to ensure it reaches you in perfect condition.'
      },
      {
        h: 'Order Tracking',
        p: <>Once your order is dispatched, you will receive a tracking link via email and SMS. You can also track your order status using the Order ID on our <L to="/track-order">Track Order</L> page.</>
      },
      {
        h: 'Delivery Attempts',
        p: 'Delhivery will attempt delivery up to 3 times. If all attempts fail, the parcel is returned to us and re-delivery charges will apply. Please ensure the contact number provided at checkout is reachable.'
      },
      {
        h: 'Damaged in Transit',
        p: <>If your parcel arrives damaged, please refer to our <L to="/legal/refund-policy">Cancellation &amp; Refund Policy</L> for the replacement process.</>
      },
      {
        h: 'Contact',
        p: <>Shipping queries? Email <L href="mailto:purchase@modshell.live">purchase@modshell.live</L> or call <L href="tel:+916374302212">+91 6374 302 212</L>.</>
      }
    ]
  },
  {
    slug: 'contact-us',
    label: 'Contact Us',
    icon: Phone,
    summary: 'How to reach us for sales, support and partnerships.',
    updated: 'February 18, 2026',
    body: [
      {
        h: 'Business Information',
        p: 'Veveru is a planter brand designing and manufacturing FRP planters in India. All business correspondence should be addressed to the details below.'
      },
      {
        h: 'Registered Address',
        p: 'SF.No 389/11, Balaji Industrial Park II,\nAnnur, Coimbatore – 641 107,\nTamil Nadu, India'
      },
      {
        h: 'Customer Support',
        p: 'For order status, delivery, returns, and general support:',
        list: [
          <>Email: <L href="mailto:purchase@modshell.live">purchase@modshell.live</L></>,
          <>Phone: <L href="tel:+916374302212">+91 6374 302 212</L></>,
          'Hours: Monday to Saturday, 10 AM – 6 PM IST'
        ]
      },
      {
        h: 'Distributor & Partnership Enquiries',
        p: <>If you're interested in becoming a Veveru distributor or reseller, please use the <L to="/partner">Partner With Us</L> form on our site, or call us directly on <L href="tel:+918754702555">+91 87547 02555</L>.</>
      },
      {
        h: 'Bulk & Custom Orders',
        p: <>For orders of 20+ planters, custom colours, sizes, or finishes, please use the <L to="/bulk-inquiry">Bulk Inquiry</L> or <L to="/custom-inquiry">Custom Order</L> form — our team will respond within 1 working day.</>
      },
      {
        h: 'Grievance Officer',
        p: <>In accordance with Indian IT Act 2000, grievances related to the website can be addressed to our grievance officer at <L href="mailto:purchase@modshell.live">purchase@modshell.live</L>.</>
      }
    ]
  }
];

// Simple intro landing page rendered at /legal
const LegalIndex = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    data-testid="legal-index"
  >
    <p className="text-xs uppercase tracking-[0.25em] text-[#bc64aa] mb-4">Legal</p>
    <h2 className="text-2xl md:text-4xl font-light text-[#641b4d] tracking-tight mb-6">
      Our policies, in plain language.
    </h2>
    <p className="text-[#7a5e75] mb-12 max-w-2xl leading-relaxed">
      Everything you need to know about how we handle your data, process payments, ship your planters, and deal with cancellations. Choose a section below, or jump straight to what you're looking for from the menu.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {sections.map(s => {
        const Icon = s.icon;
        return (
          <Link
            key={s.slug}
            to={`/legal/${s.slug}`}
            data-testid={`legal-card-${s.slug}`}
            className="group flex items-start gap-4 bg-white border border-[#E5E2DC] p-6 hover:border-[#641b4d] hover:-translate-y-0.5 transition-all"
          >
            <Icon size={22} weight="light" className="text-[#bc64aa] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-base font-medium text-[#641b4d] group-hover:text-[#bc64aa] transition-colors">{s.label}</h3>
              <p className="text-sm text-[#7a5e75] mt-1 leading-relaxed">{s.summary}</p>
            </div>
            <CaretRight size={16} weight="light" className="text-[#7a5e75] shrink-0 mt-1 group-hover:text-[#bc64aa] group-hover:translate-x-0.5 transition-all" />
          </Link>
        );
      })}
    </div>
  </motion.div>
);

const LegalDetail = ({ section }) => {
  const Icon = section.icon;
  return (
    <motion.div
      key={section.slug}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`legal-detail-${section.slug}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <Icon size={16} weight="fill" className="text-[#bc64aa]" />
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#bc64aa]">Legal · {section.label}</p>
      </div>
      <h2 className="text-2xl md:text-4xl font-light text-[#641b4d] tracking-tight mb-3">
        {section.label}
      </h2>
      <p className="text-xs text-[#7a5e75] mb-12 pb-6 border-b border-[#E5E2DC]">
        Last updated: {section.updated}
      </p>

      <div className="space-y-10">
        {section.body.map((b, i) => (
          <div key={i}>
            <h3 className="text-lg font-medium text-[#641b4d] mb-3">{b.h}</h3>
            {b.p && <p className="text-sm md:text-base text-[#7a5e75] leading-relaxed whitespace-pre-line">{b.p}</p>}
            {b.list && (
              <ul className="mt-3 space-y-2 pl-4">
                {b.list.map((li, j) => (
                  <li key={j} className="text-sm md:text-base text-[#7a5e75] leading-relaxed list-disc list-outside ml-2">
                    {li}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const Legal = () => {
  const { slug } = useParams();
  const currentSection = slug ? sections.find(s => s.slug === slug) : null;

  // Unknown slug → redirect to /legal index
  if (slug && !currentSection) {
    return <Navigate to="/legal" replace />;
  }

  return (
    <div data-testid="legal-page" className="min-h-screen bg-[#F4F4F4]">
      {/* Hero — dark green box matching Impact/FAQ style */}
      <section className="bg-[#641b4d] min-h-[50vh] flex items-center px-6 pt-24 pb-16">
        <div className="max-w-5xl mx-auto w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.3em] text-[#bc64aa] mb-8"
          >
            Legal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="legal-heading"
            className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.05]"
          >
            Transparency in every transaction.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-base md:text-lg text-white/70 mt-6 max-w-2xl font-light"
          >
            Our policies on privacy, terms, refunds, shipping, and how to reach us — all in one place.
          </motion.p>
        </div>
      </section>

      {/* Two-column layout: sidebar nav + content */}
      <section className="px-6 py-16 md:py-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-16">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#7a5e75] mb-4">Policies</p>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              <Link
                to="/legal"
                data-testid="legal-nav-index"
                className={`flex items-center gap-2 px-3 py-2.5 text-sm whitespace-nowrap transition-all border-l-2 ${
                  !slug
                    ? 'border-[#bc64aa] text-[#641b4d] font-medium bg-white'
                    : 'border-transparent text-[#7a5e75] hover:text-[#641b4d] hover:border-[#E5E2DC]'
                }`}
              >
                Overview
              </Link>
              {sections.map(s => {
                const Icon = s.icon;
                const isActive = slug === s.slug;
                return (
                  <Link
                    key={s.slug}
                    to={`/legal/${s.slug}`}
                    data-testid={`legal-nav-${s.slug}`}
                    className={`flex items-center gap-2 px-3 py-2.5 text-sm whitespace-nowrap transition-all border-l-2 ${
                      isActive
                        ? 'border-[#bc64aa] text-[#641b4d] font-medium bg-white'
                        : 'border-transparent text-[#7a5e75] hover:text-[#641b4d] hover:border-[#E5E2DC]'
                    }`}
                  >
                    <Icon size={14} weight={isActive ? 'fill' : 'light'} className={isActive ? 'text-[#bc64aa]' : ''} />
                    {s.label}
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <article className="min-w-0">
            {currentSection ? <LegalDetail section={currentSection} /> : <LegalIndex />}
          </article>
        </div>
      </section>
    </div>
  );
};

export default Legal;
